import { FC, useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
// mui components
import { Button, Grid } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
// components
import AppPrompt from 'components/app-prompt';
import { Drafts } from 'components/drafts';
import ProjectFileCabinetModal from 'components/file-cabinet-folders/modals/project-file-cabinet-modal';
import { HasPermission } from 'components/has-permission';
import AccountsButton from '../accounts-button/index';
import AuthButtons from '../auth-buttons';
import CompaniesButton from '../companies-button';
import { NavigationDropdown } from '../navigation-dropdown';
import ProfilePopperButton from '../popper-button';
import { ProjectNotification } from '../project-notification';
import ProjectsButton from '../projects-button/index';
import SettingsButton from '../settings-button';
// icons
import FileCabinetIcon from 'assets/icons/cabinet/folder-open.svg';
import HomeIcon from 'assets/icons/header/home.svg';
import ScheduleIcon from 'assets/icons/buttons/schedule-icon.svg';
import BuilderPadLogo from 'assets/icons/header/logo-builder-pad.svg';
// api
import { ICurrentPaymentPlan } from 'redux-store/api/billing';
import { ICompanyAll, useGetCompanyQuery } from 'redux-store/api/company';
import {
  PERMISSIONS_SUBJECT_ENUM,
  useGetCompanyRolePermissionsMeQuery,
} from 'redux-store/api/company-roles';
import {
  DonwloadMobileAppEnum,
  useGetProfilePreferenceQuery,
} from 'redux-store/api/profile-preference';
import { useGetProjectByIdQuery } from 'redux-store/api/projects';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import { handleSetBreadCrumbs, handleSetSearchValue } from 'redux-store/slices/file-cabinet-slice';
import { IRole, IUserMe } from 'redux-store/types';
// hooks
import { useHeaderState } from '../useHeaderState';
//styles
import { headerStyles } from '../styles/styles';
//utils
import { isBrowser } from 'react-device-detect';

interface IHeaderNewUIProps {
  isShowBillingInfo: boolean;
  currentRole: IRole;
  currentPaymentPlan: ICurrentPaymentPlan;
  companyData: ICompanyAll[];
  anchorRef: React.RefObject<HTMLButtonElement>;
  userMe: IUserMe;
}

const HeaderNewUI: FC<IHeaderNewUIProps> = ({
  isShowBillingInfo,
  currentRole,
  currentPaymentPlan,
  companyData,
  anchorRef,
  userMe,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { hash } = location;

  const [isOpenFileCabinetModal, setIsOpenFileCabinetModal] = useState(false);
  const { showHeaderActionButtons, showLogo, showProfileButtons } = useHeaderState({ userMe });
  const projectId = Number(window.location.pathname.split('/')?.[2]);
  const isSmallScreen = useMediaQuery('(max-width: 850px)');

  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();

  const { data: company } = useGetCompanyQuery(undefined, {
    skip: !userPermissions?.COMPANY_MANAGEMENT?.canRead,
  });

  const { data: preference } = useGetProfilePreferenceQuery();
  const { data: project } = useGetProjectByIdQuery(
    { id: projectId },
    { skip: !(projectId && window.location.pathname.includes('project-dashboard')) }
  );

  const props = { isShowBillingInfo, currentRole, currentPaymentPlan };

  const handleResetBreadCrumbs = useCallback(() => {
    if (location.pathname.includes('file-cabinet')) {
      dispatch(handleSetBreadCrumbs([]));
      localStorage.removeItem('fileCabinetBreadCrumbs');
    }
  }, [location]);

  useEffect(() => {
    if (!isOpenFileCabinetModal) {
      if (!!hash) {
        navigate(window.location.pathname + window.location.search, { replace: true });
      }
    }
  }, []);

  const handleOpenFileCabinet = () => {
    localStorage.setItem('isFileCabinetModalOpen', 'true');
    setIsOpenFileCabinetModal(true);
  };

  const handleCloseFileCabinetModal = useCallback(() => {
    setIsOpenFileCabinetModal(false);
    navigate(`${location.pathname}`);
    dispatch(handleSetBreadCrumbs([]));
    dispatch(handleSetSearchValue(null));
    localStorage.removeItem('fileCabinetBreadCrumbs');
    localStorage.removeItem('isFileCabinetModalOpen');
  }, [location]);

  const openAllProjectsDashboard = () => {
    if (!location.pathname.includes('calendar')) {
      navigate('/calendar');
    }

    if (location.pathname.includes('calendar')) {
      navigate('/account-dashboard');
    }
  };

  return (
    <Grid
      container
      component="header"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      sx={headerStyles.header}
    >
      <Grid sx={headerStyles.buttonsContainer}>
        {showLogo && (
          <Grid item sx={headerStyles.logoContainer}>
            <Link to={!!userMe ? '/account-dashboard' : '/sign-in'}>
              {company?.logo ? <img src={company.logo} /> : <BuilderPadLogo />}
            </Link>
          </Grid>
        )}

        {!showLogo && (
          <Grid item sx={headerStyles.logoContainerHome}>
            <Link to="/account-dashboard" onClick={handleResetBreadCrumbs}>
              <HomeIcon />
            </Link>
          </Grid>
        )}

        {companyData.length > 1 && <CompaniesButton companyData={companyData} userMe={userMe} />}

        {showHeaderActionButtons && (
          <>
            <ProjectsButton />
            <HasPermission
              type="multiple"
              permissionRules={[
                { subject: PERMISSIONS_SUBJECT_ENUM.SELECTION_MANAGE, scopes: ['canRead'] },
                { subject: PERMISSIONS_SUBJECT_ENUM.VIEW_PROJECT_FOLDERS, scopes: ['canRead'] },
              ]}
            >
              <NavigationDropdown projectId={projectId} />
            </HasPermission>
            <AccountsButton project={project} />
            <SettingsButton />
          </>
        )}
      </Grid>

      <Grid item sx={headerStyles.popperButtonContainer}>
        <Drafts />

        <Grid sx={headerStyles.actionButtons}>
          {showHeaderActionButtons && (
            <>
              <HasPermission
                type="single"
                subject={PERMISSIONS_SUBJECT_ENUM.VIEW_PROJECT_FOLDERS}
                scope="canRead"
              >
                <Grid sx={headerStyles.buttonContainer}>
                  <Button
                    ref={anchorRef}
                    variant="outlined"
                    startIcon={<FileCabinetIcon />}
                    onClick={handleOpenFileCabinet}
                    sx={headerStyles.fileCabinetButton}
                  />
                </Grid>
              </HasPermission>
              <Grid sx={headerStyles.projectNotifications(isSmallScreen)}>
                <ProjectNotification projectId={projectId} />
              </Grid>
            </>
          )}
        </Grid>

        <Grid sx={headerStyles.scheduleButtonContainer} onClick={openAllProjectsDashboard}>
          <ScheduleIcon />
        </Grid>

        {showProfileButtons ? <ProfilePopperButton {...props} /> : <AuthButtons />}
      </Grid>

      {preference?.downloadMobileApp === DonwloadMobileAppEnum.OFFER_DOWNLOAD && isBrowser && (
        <AppPrompt anchorRef={anchorRef} isProgressTrackOpen={preference?.isProgressTrackOpen} />
      )}

      {isOpenFileCabinetModal && (
        <ProjectFileCabinetModal
          open={isOpenFileCabinetModal}
          handleClose={handleCloseFileCabinetModal}
          title={project?.name}
          projectId={projectId}
          projectFolderId={project?.folderId}
        />
      )}
    </Grid>
  );
};

export default HeaderNewUI;
